import React from "react";
import { FileText, Loader2, CheckCircle2, AlertTriangle, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { DOC_TYPES, formatBytes, confidenceBand } from "@/lib/documents";

/**
 * FileCard — per-file visual summary.
 * Props:
 *   item: {
 *     localId, file, docType, status, uploadProgress,
 *     extracted_data, quality_score, error, serverDocId
 *   }
 *   onDocTypeChange(type)
 *   onRemove()
 *   onRetry()
 */
export default function FileCard({ item, onDocTypeChange, onRemove, onRetry }) {
  const {
    file,
    docType,
    status,
    uploadProgress = 0,
    error,
    quality_score,
    extracted_data,
    cached,
  } = item;

  const band = confidenceBand(quality_score);

  let statusNode;
  if (status === "selected") {
    statusNode = (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <FileText className="h-4 w-4" />
        Prêt à envoyer
      </div>
    );
  } else if (status === "uploading") {
    statusNode = (
      <div className="flex items-center gap-2 text-sm text-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
        Envoi en cours… {uploadProgress}%
      </div>
    );
  } else if (status === "analyzing") {
    statusNode = (
      <div className="flex items-center gap-2 text-sm text-foreground">
        <Sparkles className="h-4 w-4 animate-pulse-soft text-brand-gold" />
        Analyse GPT-4o (1 appel)…
      </div>
    );
  } else if (status === "analyzed") {
    statusNode = (
      <div className="flex items-center gap-2 text-sm text-foreground">
        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
        {cached ? "Récupéré du cache (aucun appel LLM)" : "Analysé avec succès"}
      </div>
    );
  } else if (status === "failed") {
    statusNode = (
      <div className="flex items-center gap-2 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4" />
        Échec : {error || "erreur inconnue"}
      </div>
    );
  } else {
    statusNode = (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">{status}</div>
    );
  }

  const selectDisabled = status !== "selected";

  return (
    <div
      data-testid={`file-card-${item.localId}`}
      className="relative rounded-lg border border-border bg-card p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-accent/60">
            <FileText className="h-5 w-5 text-brand-gold" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold" title={file.name}>
              {file.name}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{formatBytes(file.size)}</span>
              {extracted_data?.page_count ? (
                <>
                  <span>·</span>
                  <span>{extracted_data.page_count} pages</span>
                </>
              ) : null}
              {extracted_data?.currency ? (
                <>
                  <span>·</span>
                  <span>{extracted_data.currency}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {status === "failed" && onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              data-testid={`file-card-retry-${item.localId}`}
            >
              Réessayer
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={status === "uploading" || status === "analyzing"}
            aria-label="Retirer ce fichier"
            data-testid={`file-card-remove-${item.localId}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Doc type selector (only before upload) */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Type de document
          </label>
          <Select
            value={docType}
            onValueChange={onDocTypeChange}
            disabled={selectDisabled}
          >
            <SelectTrigger
              className="mt-1 h-9"
              data-testid={`file-card-doctype-${item.localId}`}
            >
              <SelectValue placeholder="Choisir un type…" />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Confidence badge (after analysis) */}
        {status === "analyzed" && (
          <div
            className={cn(
              "flex items-end justify-end text-right",
              "text-xs"
            )}
          >
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold",
                band.color === "green" &&
                  "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30",
                band.color === "orange" &&
                  "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30",
                band.color === "red" &&
                  "bg-destructive/10 text-destructive border border-destructive/30",
                band.color === "muted" && "bg-muted text-muted-foreground"
              )}
              data-testid={`file-card-confidence-${item.localId}`}
            >
              <span className="h-2 w-2 rounded-full bg-current" />
              {band.label} · {band.pct}%
            </span>
          </div>
        )}
      </div>

      {/* Progress bar during upload/analysis */}
      {(status === "uploading" || status === "analyzing") && (
        <div className="mt-4">
          <Progress
            value={status === "uploading" ? uploadProgress : 100}
            className={cn(status === "analyzing" && "animate-pulse-soft")}
            data-testid={`file-card-progress-${item.localId}`}
          />
          <div className="mt-1.5">{statusNode}</div>
        </div>
      )}

      {(status === "selected" || status === "analyzed" || status === "failed") && (
        <div className="mt-3">{statusNode}</div>
      )}
    </div>
  );
}
